// Suggestion-stats fetcher for Ads Assistant's "Suggestion" workspace area.
//
// Previously also fetched today's ads activity for the in-mode activity strip
// (now retired). Activity now lives in its own primary mode and is fed by the
// single shell-level `useAdsHeaderActivity` poll published through
// `WorkspaceUrgencyContext`. See `docs/planning/org_activity_top_level_mode.md`.

import { useCallback, useEffect, useState } from "react";
import {
  fetchAdsSuggestionStats,
  type AdsSuggestionStatsResponse,
} from "@/lib/adsAssistantClient";

export function useAdsAssistantEngagement(
  scopedOrgId: string | null,
  options: { loadSuggestionStats: boolean },
) {
  const { loadSuggestionStats } = options;
  const [statsScope, setStatsScope] = useState<"org" | "location">("org");
  const [stats, setStats] = useState<AdsSuggestionStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!scopedOrgId || !loadSuggestionStats) {
      setStats(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const s = await fetchAdsSuggestionStats(statsScope);
      if (!s.ok) {
        setError(s.error);
        setStats(null);
      } else {
        setStats(s.data);
      }
    } finally {
      setLoading(false);
    }
  }, [scopedOrgId, loadSuggestionStats, statsScope]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return {
    stats,
    statsScope,
    setStatsScope,
    engagementLoading: loading,
    engagementError: error,
    reloadEngagement: reload,
  };
}
